import React from 'react';
import { motion } from 'framer-motion';

interface LandingFeaturesProps {
  fadeInUp: any;
  staggerContainer: any;
}

export const LandingFeatures: React.FC<LandingFeaturesProps> = ({ fadeInUp, staggerContainer }) => {
  return (
    <section style={{ padding: '2rem 5%', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
        <h2 style={{ fontSize: '3rem', fontWeight: 800, letterSpacing: '-0.03em' }}>
          Built for families. Engineered for business.
        </h2>
        <p style={{ fontSize: '1.2rem', color: '#6B7280', maxWidth: '720px', margin: '1rem auto', lineHeight: 1.6 }}>
          Everything you need to secure family memories, streamline client proofing, and deliver high-res photo & video assets with zero compression.
        </p>
      </div>

      <style>{`
        .bento-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
        .bento-item { background: #FFFFFF; padding: 2.5rem; border-radius: 32px; border: 1px solid #F3F4F6; box-shadow: 0 10px 30px rgba(0,0,0,0.03); display: flex; flex-direction: column; overflow: hidden; position: relative; }
        .bento-img { width: 100%; height: 200px; object-fit: cover; border-radius: 20px; margin-bottom: 1.5rem; }
        .bento-img-side { width: 45%; height: 100%; object-fit: cover; border-radius: 20px; }
        .bento-tag { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; margin-bottom: 0.75rem; width: fit-content; }
        @media (max-width: 900px) {
          .bento-grid { grid-template-columns: 1fr; }
          .span-2 { grid-column: span 1 !important; }
          .flex-row { flex-direction: column !important; }
          .bento-img-side { width: 100%; height: 200px; margin-top: 1.5rem; }
        }
      `}</style>

      <motion.div className="bento-grid" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
        {/* ROW 1: Client Proofing & Guest Dropboxes */}
        <motion.div className="bento-item span-2" variants={fadeInUp} style={{ gridColumn: 'span 2' }}>
          <div className="flex-row" style={{ display: 'flex', gap: '2rem', alignItems: 'center', height: '100%' }}>
            <div style={{ flex: 1 }}>
              <span className="bento-tag" style={{ background: '#FEF3C7', color: '#B45309' }}>PRO & STUDIO</span>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '1rem' }}>Client Proofing & Approvals</h3>
              <p style={{ color: '#666', fontSize: '1.1rem', lineHeight: 1.6 }}>
                Deliver branded proofing galleries to clients with star ratings, one-click approvals, and photo comment threads. Eliminate expensive third-party proofing subscriptions.
              </p>
            </div>
            <img
              className="bento-img-side"
              src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=600&auto=format&fit=crop"
              alt="Client Proofing Gallery"
            />
          </div>
        </motion.div>

        <motion.div className="bento-item" variants={fadeInUp}>
          <span className="bento-tag" style={{ background: '#E0F2FE', color: '#0369A1' }}>BUSINESS & EVENTS</span>
          <img
            className="bento-img"
            src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?q=80&w=600&auto=format&fit=crop"
            alt="Guest Dropboxes"
          />
          <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>Guest Upload Dropboxes</h3>
          <p style={{ color: '#666', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Share a link with wedding guests, event attendees, or clients to collect photos and videos directly into your library without them needing an account.
          </p>
        </motion.div>

        {/* ROW 2: 4K Video Streaming & Interactive Maps */}
        <motion.div className="bento-item" variants={fadeInUp}>
          <span className="bento-tag" style={{ background: '#EDE9FE', color: '#6D28D9' }}>FULL QUALITY</span>
          <img
            className="bento-img"
            src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=600&auto=format&fit=crop"
            alt="4K Video & RAW"
          />
          <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>4K Video & RAW Streaming</h3>
          <p style={{ color: '#666', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Stream 4K clips instantly with smooth byte-range seeking. Keep 100% of camera RAW fidelity, color profiles, and EXIF metadata without compression.
          </p>
        </motion.div>

        <motion.div className="bento-item span-2" variants={fadeInUp} style={{ gridColumn: 'span 2' }}>
          <div className="flex-row" style={{ display: 'flex', gap: '2rem', alignItems: 'center', height: '100%', flexDirection: 'row-reverse' }}>
            <div style={{ flex: 1 }}>
              <span className="bento-tag" style={{ background: '#DCFCE7', color: '#15803D' }}>LOCATION EXPLORER</span>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '1rem' }}>Interactive Heatmaps & GPS</h3>
              <p style={{ color: '#666', fontSize: '1.1rem', lineHeight: 1.6 }}>
                Watch your family travels, holiday photos, and commercial location shoots come to life on a fluid Mapbox globe driven automatically by camera GPS coordinates.
              </p>
            </div>
            <img
              className="bento-img-side"
              src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=600&auto=format&fit=crop"
              alt="Map Heatmap"
            />
          </div>
        </motion.div>

        {/* ROW 3: Account vs Private Vaults & AI Culling */}
        <motion.div className="bento-item span-2" variants={fadeInUp} style={{ gridColumn: 'span 2' }}>
          <div className="flex-row" style={{ display: 'flex', gap: '2rem', alignItems: 'center', height: '100%' }}>
            <div style={{ flex: 1 }}>
              <span className="bento-tag" style={{ background: '#FEE2E2', color: '#B91C1C' }}>SECURITY & PRIVACY</span>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '1rem' }}>Personal & Account Vaults</h3>
              <p style={{ color: '#666', fontSize: '1.1rem', lineHeight: 1.6 }}>
                Every photo can be toggled between Account (shared with all family/team members) or Private (only visible to you). Protect personal documents, passports, and receipts behind a PIN-locked vault.
              </p>
            </div>
            <img
              className="bento-img-side"
              src="https://images.unsplash.com/photo-1614064641913-a53b5163f4fc?q=80&w=600&auto=format&fit=crop"
              alt="Security Vault"
            />
          </div>
        </motion.div>

        <motion.div className="bento-item" variants={fadeInUp}>
          <span className="bento-tag" style={{ background: '#FEF3C7', color: '#B45309' }}>EDGE AI</span>
          <img
            className="bento-img"
            src="https://images.unsplash.com/photo-1606148332159-245ed6eb0495?q=80&w=600&auto=format&fit=crop"
            alt="AI Culling"
          />
          <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>AI Culling Studio</h3>
          <p style={{ color: '#666', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Group bursts, duplicate shots, and blurry outtakes before uploading. Promote winners with 1 click to save cloud storage and organize shoots faster.
          </p>
        </motion.div>

        {/* ROW 4: Direct-to-Disk Takeout & Semantic Search */}
        <motion.div className="bento-item span-2" variants={fadeInUp} style={{ gridColumn: 'span 2' }}>
          <div className="flex-row" style={{ display: 'flex', gap: '2rem', alignItems: 'center', height: '100%', flexDirection: 'row-reverse' }}>
            <div style={{ flex: 1 }}>
              <span className="bento-tag" style={{ background: '#DCFCE7', color: '#15803D' }}>DATA FREEDOM</span>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '1rem' }}>Direct-to-Disk Takeout & 0 Egress</h3>
              <p style={{ color: '#666', fontSize: '1.1rem', lineHeight: 1.6 }}>
                Zero vendor lock-in. Stream 100GB+ libraries directly onto your external hard drive with no unzipping and zero browser RAM pressure, or download clean chronological ZIPs that auto-merge by Year/Month.
              </p>
            </div>
            <img
              className="bento-img-side"
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=600&auto=format&fit=crop"
              alt="Data Freedom"
            />
          </div>
        </motion.div>

        <motion.div className="bento-item" variants={fadeInUp}>
          <span className="bento-tag" style={{ background: '#EDE9FE', color: '#6D28D9' }}>SMART SEARCH</span>
          <img
            className="bento-img"
            src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=600&auto=format&fit=crop"
            alt="Face Clustering"
          />
          <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>Faces & Semantic Search</h3>
          <p style={{ color: '#666', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Search by natural language like &quot;drone sunset over ocean&quot; or tap a person&apos;s face to see every photo of them across your entire archive.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};
